import { ApplicationStatusEnum } from '~/constants/ApplicationStatus';
import type { AccountType, ApplicationStatus, RequestType } from '~/types/code/code';

/**
 * 永続化から復元した日付をDate型に変換する
 */
export const parsePersistedDate = (date: Date | string | undefined) => {
  if (!date) {
    return undefined;
  }
  if (date instanceof Date) {
    return date;
  }

  const parsedDate = new Date(date);
  return isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

/**
 * ページネーション情報
 */
export type Pagination = {
  /** 現在のページ番号 */
  currentPage: number;
  /** 1ページあたりの表示件数 */
  displayCount: number;
};

/**
 * 絞り込み条件
 */
export type ApprovalListFilterConditions = {
  /** 申請番号 */
  applicationId: string;
  /** 申請ステータス */
  applicationStatus?: ApplicationStatus | '';
  /** 申請種別 */
  requestType: RequestType | '';
  /** アカウント区分 */
  accountType: AccountType | '';
  /** 氏名（姓） */
  userNameSei: string;
  /** 氏名（名） */
  userNameMei: string;
  /** 利用駅名 */
  stationName: string;
  /** 利用者名 */
  userCompanyName: string;
  /** 申請日（From） */
  applyDateFrom?: Date;
  /** 申請日（To） */
  applyDateTo?: Date;
  /** 更新日（From） */
  updateDateFrom?: Date;
  /** 更新日（To） */
  updateDateTo?: Date;
  /** 更新者番号 */
  updateId: string;
  /** 更新者名（姓） */
  updateNameSei: string;
  /** 更新者名（名） */
  updateNameMei: string;
  /** 発行PLAT-ID */
  platId: string;
};

/**
 * アカウント承認一覧検索条件
 */
export type AccountApprovalSearchConditions = {
  /** ページネーション情報 */
  pagination: Pagination;
  /** 絞り込み条件 */
  filterConditions: ApprovalListFilterConditions;
};

/**
 * アカウント承認一覧検索条件を保持するストア
 */
export const useAccountApprovalListSearchConditionStore = defineStore('accountApprovalListSearchConditions', {
  state: (): AccountApprovalSearchConditions => ({
    pagination: {
      currentPage: 1,
      displayCount: 10,
    },
    filterConditions: {
      applicationId: '',
      applicationStatus: ApplicationStatusEnum.PENDING,
      requestType: '',
      accountType: '',
      userNameSei: '',
      userNameMei: '',
      stationName: '',
      userCompanyName: '',
      applyDateFrom: undefined,
      applyDateTo: undefined,
      updateDateFrom: undefined,
      updateDateTo: undefined,
      updateId: '',
      updateNameSei: '',
      updateNameMei: '',
      platId: '',
    },
  }),
  // Stateをセッションストレージに保存する
  persist: {
    storage: piniaPluginPersistedstate.sessionStorage(),
    afterHydrate: ({ store }) => {
      const filterConditions = (store.$state as AccountApprovalSearchConditions & {
        filterConditions: ApprovalListFilterConditions & {
          applyDateFrom?: Date | string;
          applyDateTo?: Date | string;
          updateDateFrom?: Date | string;
          updateDateTo?: Date | string;
        };
      }).filterConditions;

      filterConditions.applyDateFrom = parsePersistedDate(filterConditions.applyDateFrom);
      filterConditions.applyDateTo = parsePersistedDate(filterConditions.applyDateTo);
      filterConditions.updateDateFrom = parsePersistedDate(filterConditions.updateDateFrom);
      filterConditions.updateDateTo = parsePersistedDate(filterConditions.updateDateTo);
    },
  },
  actions: {
    /**
     * 検索条件をリセットする
     */
    resetSearchConditions() {
      this.$reset();
    },
  },
});
